import { theme, Badge, Space, Typography } from "antd";
import { CustomTooltip } from "src/components/CustomTooltip";
export type StatusType = "improved" | "regressed" | "neutral";

interface StatusIndicatorProps {
  status?: StatusType;
  showText?: boolean;
}

export const StatusIndicator = ({
  status,
  showText = true,
}: StatusIndicatorProps) => {
  const { token } = theme.useToken();

  if (!status) return null;

  return (
    <Badge
      status={
        status === "improved"
          ? "success"
          : status === "regressed"
          ? "error"
          : "default"
      }
      text={
        showText && (
          <Typography.Text
            className="vzbl-text-3"
            style={{ fontSize: token.fontSizeSM }}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </Typography.Text>
        )
      }
    />
  );
};

interface StatusIndicatorHeaderProps {
  title: string;
  description?: string;
  status?: StatusType;
}

export const StatusIndicatorHeader = ({
  title,
  description,
  status,
}: StatusIndicatorHeaderProps) => {
  const { token } = theme.useToken();

  return (
    <div
      style={{ display: "flex", flexDirection: "column", gap: token.marginXS }}
    >
      <div
        style={{ display: "flex", alignItems: "center", gap: token.marginXS }}
      >
        <Typography.Title level={4} className="m-0">
          {title}
        </Typography.Title>
        <StatusIndicator status={status} />
      </div>
      {description && (
        <Typography.Text
          className="vzbl-text-3"
          ellipsis
          style={{ fontSize: token.fontSizeSM }}
        >
          {description}
        </Typography.Text>
      )}
    </div>
  );
};

interface StatusIndicatorSectionProps {
  title?: string;
  children: React.ReactNode;
  status?: StatusType;
  tooltip?: boolean;
  className?: string;
}

export const StatusIndicatorSection = ({
  title,
  children,
  status,
  tooltip = true,
  className,
}: StatusIndicatorSectionProps) => {
  const { token } = theme.useToken();

  return (
    <div
      className={className}
      style={{
        padding: token.paddingSM,
        borderRadius: token.borderRadiusSM,
        borderLeft: `3px solid ${
          status
            ? status === "improved"
              ? token.colorSuccess
              : status === "regressed"
              ? token.colorError
              : token.colorTextTertiary
            : "transparent"
        }`,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {title && (
        <Typography.Text
          className="vzbl-header-4"
          style={{ display: "block", marginBottom: token.marginXS }}
        >
          {title}
        </Typography.Text>
      )}
      {tooltip ? (
        <CustomTooltip content={children} size="large">
          <div
            style={{
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {children}
          </div>
        </CustomTooltip>
      ) : (
        children
      )}
    </div>
  );
};
